/**
 * Catálogos de opciones y etiquetas. COMPARTIDO ENTRE SERVIDOR Y BROWSER.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REGLA DE ESTE ARCHIVO (y de todo src/shared/): SOLO `export type`, `interface`,
 * literales `export const` y funciones puras.
 *
 * NADA de `require`, nada de imports fuera de src/shared/, nada de globals de Node
 * (`process`, `__dirname`, `Buffer`) ni de DOM (`window`, `document`).
 *
 * Por qué: este mismo FUENTE se compila DOS VECES — `tsc` lo emite a CommonJS en
 * dist/shared/ para el bot, y Vite/esbuild lo compila a ESM dentro del bundle del
 * browser. No se comparte ningún artefacto compilado, así que la única forma de que
 * el mecanismo sea seguro es que el archivo no toque nada específico de un entorno.
 *
 * Es también la razón por la que estos catálogos NO pueden quedarse en config.ts:
 * ese archivo lee `process.env` en el top level (APPROVER_SLACK_USER_IDS,
 * DEFAULT_TIMEZONE) y en el browser `process` no existe.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * config.ts los re-exporta, así que blocks.ts y parse.ts siguen importando de ahí
 * sin cambios y la dirección de dependencias que documenta CLAUDE.md queda igual.
 */

// ── Toggle del campo "Format" (In person / Virtual) ───────────────────────────
// El PDF lo incluye; el alcance MVP de Todoist no lo lista. Poner en `false` lo
// elimina del modal, del mensaje de aprobación, del DM de resultado y del insert.
// NO requiere migración: meeting_requests.format es nullable.
//
// OJO en el dashboard: el tablero MUESTRA `format` siempre que la columna no sea
// null (las filas históricas conservan su valor aunque después se apague el
// toggle); lo único que se gatea con esta constante es el CHIP DE FILTRO.
export const INCLUDE_FORMAT = true

export interface Option {
  value: string
  label: string
}

export const ACTIVITY_TYPES: Option[] = [
  { value: "formal", label: "Formal" },
  { value: "informal", label: "Informal" },
  { value: "training", label: "Training" },
  { value: "recurrent", label: "Recurrent" },
]

/**
 * El único activity_type que tiene cadencia. Constante y no literal suelto porque
 * lo comparan parse.ts (exigir la frecuencia), app.ts (crear la recurrencia) y
 * blocks.ts: un typo en cualquiera de los tres desactivaría la recurrencia en
 * silencio. Debe coincidir con el `value` de arriba.
 */
export const RECURRENT_ACTIVITY = "recurrent"

/**
 * Cadencia de una solicitud `Recurrent`. Las tres se anclan a la fecha propuesta:
 *
 *   daily    → solo días hábiles, Lun–Vie (no se ancla a un día en particular)
 *   weekly   → mismo día de la semana (day_of_week)
 *   monthly  → mismo día del mes (day_of_month), con clamp al último día en los
 *              meses cortos: el 31 cae 28/29 feb y 30 abr, y vuelve al 31 en marzo
 *
 * Los `value` deben coincidir con el CHECK de meeting_recurrences.frequency y con
 * recurrence_matches() en supabase/schema.sql — esa función SQL es la única
 * definición de "cuándo recurre esto".
 */
export const FREQUENCIES: Option[] = [
  { value: "daily", label: "Daily (Mon–Fri)" },
  { value: "weekly", label: "Weekly (Same weekday)" },
  { value: "monthly", label: "Monthly (Same day of month)" },
]

/** `value` = minutos en string → se guarda como INTEGER para poder sumar. */
export const DURATIONS: Option[] = [
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "60", label: "60 min" },
  { value: "120", label: "120 min" },
]

export const FORMATS: Option[] = [
  { value: "in_person", label: "In person" },
  { value: "virtual", label: "Virtual" },
]

/**
 * Estados de una solicitud. Deben coincidir con el CHECK de meeting_requests.status
 * en schema.sql. Existen como catálogo —y no como unión de literales suelta— para
 * que el dashboard valide el filtro `status` con el mismo isValidOption() que usa
 * el bot, y para que los chips del SPA no puedan divergir del CHECK de la base.
 */
export const REQUEST_STATUSES: Option[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "not_approved", label: "Not approved" },
]

/**
 * Estados de una ocurrencia (meeting_occurrences.status). Hasta que existió el
 * dashboard NADA en src/ los escribía: los ponía el generador en 'scheduled' y
 * había que editarlos a mano en Supabase. El PATCH de ocurrencias valida contra
 * este catálogo EN NODE; el CHECK de schema.sql queda como backstop, pero produce
 * un error opaco de Postgres en vez de un 400 usable.
 */
export const OCCURRENCE_STATUSES: Option[] = [
  { value: "scheduled", label: "Scheduled" },
  { value: "held", label: "Held" },
  { value: "skipped", label: "Skipped" },
  { value: "cancelled", label: "Cancelled" },
]

/**
 * Etiquetas visibles. Los MISMOS textos se usan en el modal, en el DM de
 * aprobación y en el detalle del dashboard — es lo que hace posible que un admin
 * cruce una fila del tablero contra el DM que recibió en Slack. Por eso NO se
 * traducen, aunque el resto de la UI del tablero esté en español.
 */
export const LABELS = {
  requester: "Requester name",
  requestedAt: "Request date",
  activityType: "Activity type",
  frequency: "Recurrence frequency",
  topic: "Topic / objective",
  participants: "Participants",
  proposedDate: "Proposed date",
  proposedTime: "Proposed time",
  proposed: "Proposed date and time",
  duration: "Estimated duration",
  format: "Format",
  justification: "Justification (why it must take place during work hours)",
} as const

/** Valor interno → etiqueta visible. Si el valor no está en el catálogo, se muestra crudo. */
export function labelFor(options: Option[], value: string | null): string {
  return options.find((o) => o.value === value)?.label ?? value ?? "—"
}

/** true si `value` pertenece al catálogo (defensa ante un payload manipulado). */
export function isValidOption(options: Option[], value: string | null | undefined): boolean {
  return options.some((o) => o.value === value)
}

/** Los `value` de un catálogo. Para armar allowlists sin repetir los literales. */
export function optionValues(options: Option[]): string[] {
  return options.map((o) => o.value)
}
